import React, { useState, useEffect, useRef } from 'react';
import { Mail, MessageCircle, Instagram, Linkedin, Clock, Copy, Check, Send, CheckCircle2, Twitter, User, Building2, HelpCircle, MessageSquare, ArrowRight } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import PublicNavbar from '@/components/landing/PublicNavbar';
import PublicFooter from '@/components/landing/PublicFooter';
import { useI18n } from '@/lib/I18nContext';

function useIntersection(ref, threshold = 0.15) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

const CONTACT_EMAIL = 'ventas@bezkraen.com';
const WHATSAPP_NUMBER = '+56912345678';

const HELP_OPTION_KEYS = [
  'contacts.help_demo',
  'contacts.help_technical',
  'contacts.help_pricing',
  'contacts.help_other',
];

function DrawLine() {
  return (
    <div className="relative h-1 w-48 mx-auto mt-4 overflow-hidden rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
      <div
        className="h-full rounded-full"
        style={{
          background: 'linear-gradient(90deg, #00D5FF, #7C3AED)',
          animation: 'drawLine 1s ease 0.5s forwards',
          width: 0,
        }}
      />
      <style>{`@keyframes drawLine { to { width: 100%; } }`}</style>
    </div>
  );
}

export default function Contacto() {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', company: '', topic: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState(false);
  const formRef = useRef(null);
  const formVisible = useIntersection(formRef);

  function handleCopy() {
    navigator.clipboard.writeText(CONTACT_EMAIL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSending(true);
    setSendError(false);
    try {
      const res = await base44.functions.invoke('sendContactEmail', {
        name: form.name,
        email: form.email,
        company: form.company,
        topic: form.topic,
        message: form.message,
      });
      if (res?.data?.success) {
        setSent(true);
      } else {
        setSendError(true);
      }
    } catch (_) {
      setSendError(true);
    }
    setSending(false);
  }

  const SOCIALS = [
    { icon: Instagram, label: '@bezkraen', href: 'https://instagram.com/bezkraen', color: '#E1306C' },
    { icon: Linkedin, label: 'Bezkraen', href: 'https://linkedin.com/company/bezkraen', color: '#0A66C2' },
    { icon: Twitter, label: '@bezkraen', href: 'https://twitter.com/bezkraen', color: '#1DA1F2' },
  ];

  const inputStyle = {
    background: '#0A0F1C',
    border: '1.5px solid #1E2D45',
    color: '#F8FAFC',
    borderRadius: 10,
    padding: '12px 16px 12px 42px',
    fontSize: 14,
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  };

  return (
    <div className="min-h-screen font-body overflow-x-hidden" style={{ background: '#070B14', color: '#F8FAFC' }}>
      <PublicNavbar />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20 px-6 text-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #070B14 0%, #0d1525 50%, #111827 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(0,213,255,0.1) 0%, transparent 60%)' }} />
        <div className="relative z-10 max-w-2xl mx-auto">
          <h1
            className="font-heading font-bold text-white mb-4"
            style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', lineHeight: 1.15 }}
          >
            {t('contacts.talk')}
          </h1>
          <DrawLine />
          <p
            className="text-lg mt-6 leading-relaxed"
            style={{ color: '#94A3B8', animation: 'fadeIn 0.8s ease 0.4s forwards', opacity: 0, animationFillMode: 'forwards' }}
          >
            {t('contacts.subtitle')}
          </p>
        </div>
        <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }`}</style>
      </section>

      {/* Main content */}
      <section className="py-20 px-6" style={{ background: '#0d1525' }}>
        <style>{`
          @keyframes shimmerBar { 0% { background-position: 0% 0; } 100% { background-position: 200% 0; } }
          @keyframes iconPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
          @keyframes fieldSlideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes cardSlideIn { from { opacity: 0; transform: translateX(-16px); } to { opacity: 1; transform: translateX(0); } }
          @keyframes scaleIn { from { transform: scale(0.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          .contact-input:focus { border-color: #00D5FF !important; box-shadow: 0 0 0 3px rgba(0,213,255,0.12) !important; }
          .contact-input::placeholder { color: #94A3B8; }
          .contact-card-hover:hover { border-color: rgba(0,213,255,0.25) !important; box-shadow: 0 8px 30px rgba(0,213,255,0.06) !important; }
        `}</style>
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* Left: Contact info */}
          <div className="space-y-8">
            <div>
              <h2 className="font-heading font-bold text-2xl mb-6" style={{ color: '#F8FAFC', animation: 'cardSlideIn 0.5s ease forwards', opacity: 0, animationDelay: '0s' }}>{t('contacts.contact_info')}</h2>

              {/* Email */}
              <div className="contact-card-hover group flex items-center gap-4 p-4 rounded-2xl hover:shadow-lg transition-all mb-4 cursor-default" style={{ background: '#111827', border: '1px solid #1E2D45', animation: 'cardSlideIn 0.5s ease forwards', opacity: 0, animationDelay: '0.1s' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110" style={{ background: 'linear-gradient(135deg, #00D5FF, #7C3AED)' }}>
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#475569' }}>{t('contacts.email')}</p>
                  <p className="font-medium truncate" style={{ color: '#F8FAFC' }}>{CONTACT_EMAIL}</p>
                </div>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
                  style={{ background: copied ? 'rgba(34,197,94,0.15)' : 'rgba(0,213,255,0.1)', color: copied ? '#22C55E' : '#00D5FF' }}
                >
                  {copied ? <><Check className="w-3.5 h-3.5" />{t('common.copied')}</> : <><Copy className="w-3.5 h-3.5" />{t('common.copy')}</>}
                </button>
              </div>

              {/* WhatsApp */}
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-card-hover group flex items-center gap-4 p-4 rounded-2xl hover:shadow-lg transition-all mb-6"
                style={{ background: '#111827', border: '1px solid #1E2D45', animation: 'cardSlideIn 0.5s ease forwards', opacity: 0, animationDelay: '0.2s' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110" style={{ background: '#25D366' }}>
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#475569' }}>{t('contacts.whatsapp')}</p>
                  <p className="font-medium" style={{ color: '#F8FAFC' }}>{WHATSAPP_NUMBER}</p>
                </div>
                <span className="text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ background: 'rgba(34,197,94,0.12)', color: '#22C55E' }}>{t('contacts.open_chat')}</span>
              </a>

              {/* Socials */}
              <div className="flex gap-3" style={{ animation: 'cardSlideIn 0.5s ease forwards', opacity: 0, animationDelay: '0.3s' }}>
                {SOCIALS.map(({ icon: Icon, label, href, color }) => (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col items-center gap-1.5 p-3 rounded-xl hover:-translate-y-1 transition-all duration-300"
                    style={{ minWidth: 80, background: '#111827', border: '1px solid #1E2D45' }}
                  >
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: color + '20' }}>
                      <Icon className="w-4 h-4" style={{ color }} />
                    </div>
                    <span className="text-xs" style={{ color: '#94A3B8' }}>{label}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Response time card */}
            <div className="contact-card-hover flex items-center gap-4 p-5 rounded-2xl" style={{ background: '#111827', border: '1px solid #1E2D45', animation: 'cardSlideIn 0.5s ease forwards', opacity: 0, animationDelay: '0.4s' }}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(0,213,255,0.1)' }}>
                <Clock className="w-5 h-5" style={{ color: '#00D5FF', animation: 'iconPulse 2s ease-in-out infinite' }} />
              </div>
              <div>
                <p className="font-semibold" style={{ color: '#F8FAFC' }}>{t('contacts.response_time')}</p>
                <p className="text-sm mt-0.5" style={{ color: '#94A3B8' }}>{t('contacts.response_time_value')}</p>
              </div>
            </div>
          </div>

          {/* Right: Form — tarjeta oscura, consistente con el resto de la pagina */}
          <div ref={formRef} style={{ transition: 'opacity 0.6s ease, transform 0.6s ease', opacity: formVisible ? 1 : 0, transform: formVisible ? 'translateX(0)' : 'translateX(24px)' }}>
            <div className="rounded-2xl overflow-hidden" style={{ background: '#131C2E', border: '1px solid #1E2D45', boxShadow: '0 20px 60px -20px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,213,255,0.06)' }}>
              {/* Animated gradient top accent */}
              <div style={{ height: 4, background: 'linear-gradient(90deg, #00D5FF, #7C3AED, #00D5FF)', backgroundSize: '200% auto', animation: 'shimmerBar 3s linear infinite' }} />
              <div className="p-8">
                <div className="flex items-center gap-2.5 mb-6">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #00D5FF, #7C3AED)', animation: 'iconPulse 2.5s ease-in-out infinite' }}>
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-xl" style={{ color: '#F8FAFC' }}>{t('contacts.write_us')}</h2>
                    <p className="text-xs" style={{ color: '#94A3B8' }}>{t('contacts.write_us_subtitle', 'Respondemos en menos de 24 horas')}</p>
                  </div>
                </div>

                {sent ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.12)', animation: 'scaleIn 0.4s ease' }}>
                      <CheckCircle2 className="w-8 h-8" style={{ color: '#22C55E' }} />
                    </div>
                    <p className="font-heading font-bold text-xl" style={{ color: '#F8FAFC' }}>{t('contacts.sent')}</p>
                    <p className="text-center" style={{ color: '#94A3B8' }}>{t('contacts.sent_subtitle')}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {sendError && (
                      <div className="rounded-xl px-4 py-3 text-sm flex items-start gap-2" style={{ background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(248,113,113,0.35)', color: '#FCA5A5' }}>
                        <span className="shrink-0">⚠️</span>
                        <span>
                          {t('contacts.send_error', 'No pudimos enviar tu mensaje. Probá de nuevo en unos segundos, o escribinos directo a ')}
                          <a href={`mailto:${CONTACT_EMAIL}`} className="underline font-semibold">{CONTACT_EMAIL}</a>.
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div style={{ animation: 'fieldSlideUp 0.4s ease forwards', opacity: 0, animationDelay: '0.1s' }}>
                        <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#94A3B8' }}>{t('contacts.name')} *</label>
                        <div className="relative">
                          <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94A3B8' }} />
                          <input
                            required
                            value={form.name}
                            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                            className="contact-input"
                            style={inputStyle}
                            placeholder={t('contacts.placeholder_name')}
                          />
                        </div>
                      </div>
                      <div style={{ animation: 'fieldSlideUp 0.4s ease forwards', opacity: 0, animationDelay: '0.18s' }}>
                        <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#94A3B8' }}>{t('contacts.email')} *</label>
                        <div className="relative">
                          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94A3B8' }} />
                          <input
                            required
                            type="email"
                            value={form.email}
                            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                            className="contact-input"
                            style={inputStyle}
                            placeholder={t('contacts.placeholder_email')}
                          />
                        </div>
                      </div>
                    </div>

                    <div style={{ animation: 'fieldSlideUp 0.4s ease forwards', opacity: 0, animationDelay: '0.26s' }}>
                      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#94A3B8' }}>{t('contacts.company')} <span className="normal-case" style={{ color: '#64748B' }}>{t('contacts.optional')}</span></label>
                      <div className="relative">
                        <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94A3B8' }} />
                        <input
                          value={form.company}
                          onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                          className="contact-input"
                          style={inputStyle}
                          placeholder={t('contacts.placeholder_company')}
                        />
                      </div>
                    </div>

                    <div style={{ animation: 'fieldSlideUp 0.4s ease forwards', opacity: 0, animationDelay: '0.34s' }}>
                      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#94A3B8' }}>{t('contacts.topic')} *</label>
                      <div className="relative">
                        <HelpCircle className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: '#94A3B8' }} />
                        <select
                          required
                          value={form.topic}
                          onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                          className="contact-input"
                          style={{ ...inputStyle, appearance: 'none', padding: '12px 40px 12px 42px', backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2394A3B8%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%2F%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center' }}
                        >
                          <option value="" style={{ background: '#131C2E' }}>{t('contacts.help_select')}</option>
                          {HELP_OPTION_KEYS.map(k => <option key={k} value={t(k)} style={{ background: '#131C2E' }}>{t(k)}</option>)}
                        </select>
                      </div>
                    </div>

                    <div style={{ animation: 'fieldSlideUp 0.4s ease forwards', opacity: 0, animationDelay: '0.42s' }}>
                      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#94A3B8' }}>{t('contacts.message')} *</label>
                      <div className="relative">
                        <MessageSquare className="absolute left-3.5 top-3.5 w-4 h-4" style={{ color: '#94A3B8' }} />
                        <textarea
                          required
                          value={form.message}
                          onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                          rows={4}
                          className="contact-input"
                          style={{ ...inputStyle, resize: 'none', padding: '12px 16px 12px 42px' }}
                          placeholder={t('contacts.placeholder_message')}
                        />
                      </div>
                    </div>

                    <div style={{ animation: 'fieldSlideUp 0.4s ease forwards', opacity: 0, animationDelay: '0.5s' }}>
                      <button
                        type="submit"
                        disabled={sending}
                        className="w-full flex items-center justify-center gap-2 font-bold py-3.5 rounded-xl transition-all hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{ background: 'linear-gradient(135deg, #00D5FF, #7C3AED)', color: '#070B14', boxShadow: '0 4px 20px rgba(0,213,255,0.3)', position: 'relative', overflow: 'hidden' }}
                      >
                        {sending ? (
                          <><div className="w-4 h-4 border-2 border-black/30 border-t-black/80 rounded-full animate-spin" />{t('contacts.sending')}</>
                        ) : (
                          <><Send className="w-4 h-4" />{t('contacts.send')} <ArrowRight className="w-4 h-4" /></>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Remote work section */}
      <section className="py-16 px-6" style={{ background: '#070B14' }}>
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: '#00D5FF' }}>{t('contacts.presence')}</p>
          <h2 className="font-heading font-bold text-3xl text-white mb-4">
            {t('contacts.remote_title')}
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#94A3B8' }}>
            {t('contacts.remote_desc')}
          </p>
          <div className="mt-8 flex justify-center">
            <svg viewBox="0 0 320 420" className="w-64 h-auto opacity-90" fill="none">
              <style>{`
                .hex {
                  fill: #111827;
                  stroke: #1E2D45;
                  stroke-width: 0.8;
                  animation: hexLight 4.8s ease-in-out infinite;
                }
                .hex:hover { fill: #1a2235; stroke: #00D5FF; }
                .dot {
                  animation: dotPulse 4.8s ease-in-out infinite;
                }
                @keyframes hexLight {
                  0%, 100% { fill: #111827; stroke: #1E2D45; }
                  8%, 16% { fill: #0d1525; stroke: rgba(0,213,255,0.6); }
                }
                @keyframes dotPulse {
                  0%, 100% { opacity: 0.35; r: 3; }
                  8%, 16% { opacity: 1; r: 5; }
                }
              `}</style>
              <path className="hex" style={{ animationDelay: '0s' }} d="M100 20 L160 10 L180 40 L170 80 L140 90 L110 70 Z" />
              <path className="hex" style={{ animationDelay: '0.6s' }} d="M60 80 L110 70 L140 90 L130 130 L90 140 L60 120 Z" />
              <path className="hex" style={{ animationDelay: '0.6s' }} d="M130 90 L170 80 L200 110 L190 150 L150 160 L130 130 Z" />
              <path className="hex" style={{ animationDelay: '1.2s' }} d="M80 140 L130 130 L150 160 L140 210 L100 220 L75 190 Z" />
              <path className="hex" style={{ animationDelay: '1.2s' }} d="M140 160 L190 150 L210 180 L200 230 L160 240 L140 210 Z" />
              <path className="hex" style={{ animationDelay: '1.8s' }} d="M90 220 L140 210 L160 240 L150 300 L110 310 L85 270 Z" />
              <path className="hex" style={{ animationDelay: '1.8s' }} d="M150 240 L200 230 L220 260 L210 320 L170 330 L150 300 Z" />
              <path className="hex" style={{ animationDelay: '2.4s' }} d="M100 310 L150 300 L170 330 L160 390 L120 400 L95 360 Z" />
              <circle className="dot" cx="130" cy="55" r="3" fill="#00D5FF" style={{ animationDelay: '0s' }} />
              <circle className="dot" cx="115" cy="105" r="3" fill="#00D5FF" style={{ animationDelay: '0.6s' }} />
              <circle className="dot" cx="165" cy="120" r="3" fill="#00D5FF" style={{ animationDelay: '0.6s' }} />
              <circle className="dot" cx="120" cy="175" r="3" fill="#00D5FF" style={{ animationDelay: '1.2s' }} />
              <circle className="dot" cx="175" cy="195" r="3" fill="#00D5FF" style={{ animationDelay: '1.2s' }} />
              <circle className="dot" cx="130" cy="265" r="3" fill="#7C3AED" style={{ animationDelay: '1.8s' }} />
            </svg>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}